import { useEffect, useState } from "react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Receipt, XCircle, ArrowCounterClockwise } from "@phosphor-icons/react";

const fmtDate = (d) => (d ? new Date(d).toLocaleDateString("fr-FR") : null);

export const SubscriptionManager = () => {
  const { refresh } = useAuth();
  const [sub, setSub] = useState(null);
  const [busy, setBusy] = useState(false);
  const [confirm, setConfirm] = useState(false);

  const load = async () => {
    try { const { data } = await api.get("/payments/subscription"); setSub(data); } catch { setSub(null); }
  };
  useEffect(() => { load(); }, []);

  const act = async (path, ok) => {
    setBusy(true);
    try { await api.post(path); toast.success(ok); await load(); refresh(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); setConfirm(false); }
  };

  const portal = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/payments/portal", { origin_url: window.location.origin });
      window.location.href = data.url;
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); setBusy(false); }
  };

  if (!sub) return null;
  const end = fmtDate(sub.current_period_end);

  return (
    <div className="mt-5 pt-5 border-t border-border space-y-3" data-testid="subscription-manager">
      {sub.cancel_at_period_end ? (
        <p className="text-sm text-amber-500" data-testid="subscription-cancel-scheduled">
          Résiliation programmée : votre accès reste actif jusqu'au <b>{end}</b>, aucun nouveau prélèvement ne sera effectué.
        </p>
      ) : sub.has_subscription && end && (
        <p className="text-sm text-muted" data-testid="subscription-renewal">Prochain renouvellement le <span className="text-accent">{end}</span>.</p>
      )}
      <div className="flex flex-wrap gap-3">
        {sub.can_manage_billing && (
          <button onClick={portal} disabled={busy} data-testid="billing-portal-btn"
            className="flex items-center gap-2 text-sm border border-border text-fg rounded-md px-4 py-2 hover-lift disabled:opacity-60">
            <Receipt size={15} /> Gérer ma facturation
          </button>
        )}
        {sub.can_cancel && !sub.cancel_at_period_end && (
          <button onClick={() => setConfirm(true)} disabled={busy} data-testid="subscription-cancel-btn"
            className="flex items-center gap-2 text-sm border border-destructive/40 text-destructive rounded-md px-4 py-2 hover:bg-destructive/10 transition-colors disabled:opacity-60">
            <XCircle size={15} /> Résilier mon abonnement
          </button>
        )}
        {sub.can_cancel && sub.cancel_at_period_end && (
          <button onClick={() => act("/payments/subscription/resume", "Résiliation annulée — votre abonnement continue.")} disabled={busy}
            data-testid="subscription-resume-btn"
            className="flex items-center gap-2 text-sm border border-accent/40 text-accent rounded-md px-4 py-2 hover:bg-accent/10 transition-colors disabled:opacity-60">
            <ArrowCounterClockwise size={15} /> Annuler la résiliation
          </button>
        )}
      </div>

      <AlertDialog open={confirm} onOpenChange={setConfirm}>
        <AlertDialogContent className="bg-surface border-border" data-testid="subscription-cancel-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Résilier votre abonnement ?</AlertDialogTitle>
            <AlertDialogDescription>
              Sans pénalité. Votre accès reste actif jusqu'à la fin de la période payée{end ? ` (${end})` : ""}, puis l'abonnement ne sera pas renouvelé. Vos données sont conservées.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="subscription-cancel-dismiss">Garder mon abonnement</AlertDialogCancel>
            <AlertDialogAction data-testid="subscription-cancel-confirm"
              onClick={(e) => { e.preventDefault(); act("/payments/subscription/cancel", "Résiliation enregistrée — accès maintenu jusqu'à la fin de la période payée."); }}
              className="bg-destructive text-white hover:bg-destructive/90">
              {busy ? "Résiliation…" : "Confirmer la résiliation"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};
